import { inject, Injectable } from '@angular/core';
import { QuoteToolLiteState } from '../store';
import { QuoteService } from '@lx/quote-tool/shared/data-access/quote';
import { AnalyticsService } from '@lx/quote-tool/shared/services';
import { QuoteLeadInputV5, QuoteLeadOrigin, QuoteLeadOriginEnum, QuoteLeadOutputV5, QuoteLeadQuoteInput } from '@lx/quote-tool/shared/types';
import { Observable } from 'rxjs';
import { getLocale } from '@lx/shared/i18n';
import { getDateStringFromDate, getToday } from '@lx/shared/utils';
import { QuoteInputMapper } from './quote-input.mapper';
import { withBaseLeadPayload } from '../mixins';
import { Flows } from '@lx/quote-tool/lite/shared/types';
import { formatPreferredClassTime } from '@lx/quote-tool/lite/shared/utils';

@Injectable({
  providedIn: 'root',
})
export class LeadService {
  private readonly quoteService = inject(QuoteService);
  private readonly quoteInputMapper = inject(QuoteInputMapper);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly locale = getLocale();
  private readonly buildBaseLeadPayload = withBaseLeadPayload({
    analyticsService: this.analyticsService,
    locale: this.locale,
  }).buildBaseLeadPayload;

  createLead(state: Partial<QuoteToolLiteState>, formOrigin?: QuoteLeadOrigin): Observable<QuoteLeadOutputV5> {
    return this.quoteService.createLeadV5(this.buildLeadPayload(state, formOrigin));
  }

  private buildLeadPayload(state: Partial<QuoteToolLiteState>, formOrigin?: QuoteLeadOrigin): QuoteLeadInputV5 {
    return {
      ...this.buildBaseLeadPayload(state, formOrigin),
      quote: this.mapStateToQuote(state, formOrigin),
    };
  }

  private mapStateToQuote(state: Partial<QuoteToolLiteState>, formOrigin?: QuoteLeadOrigin): QuoteLeadQuoteInput {
    const quoteIdResetRequired = formOrigin !== QuoteLeadOriginEnum.ONLINE_COURSES_ENROLMENT && state.activeFlow === Flows.Online;
    const {
      selectedLanguage,
      selectedSchoolLocation,
      selectedCourse,
      selectedDuration,
      selectedStartDate,
      selectedEndDate,
      selectedCurrency,
      selectedClassTime,
      selectedTimezone,
    } = state;
    const preferredClassTime = formatPreferredClassTime(selectedClassTime);

    return {
      headerData: {
        language: selectedLanguage,
        school: selectedSchoolLocation?.schoolName,
        course: selectedCourse?.name,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        selectedCurrency: selectedCurrency,
        bookingDate: getDateStringFromDate(getToday()),
        duration: selectedDuration,
        classTimes: preferredClassTime ? [preferredClassTime] : undefined,
        userClassTimes: this.quoteInputMapper.mapOnlineUserClassTimes(selectedClassTime, selectedTimezone),
      },
      quoteInput: {
        ...this.quoteInputMapper.mapQuoteInput(state),
        ...(quoteIdResetRequired ? { quoteId: undefined } : {}),
      },
    };
  }
}
